/**
 * Data layer for `advertiser-billing`. Thin typed fetch wrappers over /api/v1/advertiser-billing.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Ported from pre-nuke legacy settings/billing.tsx; modernized for Advertiser
 * Billing domain: advertiser roster with balance, cycle, last invoice, status,
 * plus invoice drill-through and Generate-Invoice action.
 */

export type BillingCycle = "monthly" | "weekly" | "net30";

export type AdvertiserStatus =
	| "active"
	| "past_due"
	| "suspended"
	| "closed";

export type InvoiceStatus =
	| "paid"
	| "open"
	| "void"
	| "uncollectible"
	| "draft";

export interface AdvertiserBillingRow {
	id: string;
	tenant_id: string;
	advertiser_name: string;
	current_balance_cents: number;
	billing_cycle: BillingCycle;
	last_invoice_at: number | null;
	last_invoice_amount_cents: number | null;
	status: AdvertiserStatus;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface InvoiceRow {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	number: string;
	amount_cents: number;
	status: InvoiceStatus;
	created_at: number;
	pdf_url?: string | null;
	hosted_url?: string | null;
}

export interface ListAdvertiserBillingResponse {
	count: number;
	items: AdvertiserBillingRow[];
}

export interface ListInvoicesResponse {
	count: number;
	items: InvoiceRow[];
}

export interface CreateAdvertiserBillingBody {
	advertiser_name?: string;
	billing_cycle?: BillingCycle;
	[key: string]: unknown;
}

export interface GenerateInvoiceBody {
	amount_cents?: number;
	[key: string]: unknown;
}

const BASE = "/api/v1/advertiser-billing";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAdvertiserBillingResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAdvertiserBillingResponse>(res);
}

export async function create(body: CreateAdvertiserBillingBody): Promise<AdvertiserBillingRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<AdvertiserBillingRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export async function listInvoices(advertiserId: string): Promise<ListInvoicesResponse> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(advertiserId)}/invoices`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<ListInvoicesResponse>(res);
}

export async function generateInvoice(
	advertiserId: string,
	body?: GenerateInvoiceBody,
): Promise<InvoiceRow> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(advertiserId)}/invoices`,
		{
			method: "POST",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify(body ?? {}),
		},
	);
	return parseOrThrow<InvoiceRow>(res);
}

export const advertiserBillingQueryKeys = {
	all: ["advertiser-billing"] as const,
	list: () => [...advertiserBillingQueryKeys.all] as const,
	detail: (id: string) => [...advertiserBillingQueryKeys.all, id] as const,
	invoices: (id: string) =>
		[...advertiserBillingQueryKeys.all, id, "invoices"] as const,
};
